/**
 * Reusable "integration not configured" error toast.
 *
 * When an action fails because a required provider/API/token isn't set up, this
 * surfaces the honest server reason PLUS an actionable in-app link to that
 * integration's setup page (and, optionally, an external link to obtain the
 * credentials). It degrades to a plain error toast for non-config failures.
 *
 * Reusable across modules — GPS provider, AI providers, and any future
 * integration. The caller supplies the setup destination + the component's
 * `navigate` (react-hot-toast renders outside the Router, so it cannot call
 * `useNavigate` itself).
 */
import { toast } from 'react-hot-toast';

import { extractApiErrorMessage } from '../services/api';

type NavigateFn = (path: string) => void;

export interface IntegrationErrorToastOptions {
  /** From the calling component's `useNavigate()`. */
  navigate: NavigateFn;
  /** In-app route to the integration's setup page, e.g. '/settings?category=gps-providers'. */
  setupPath: string;
  /** Link text, e.g. 'Set up GPS credentials'. */
  setupLabel: string;
  /** Shown when the server gives no message. */
  fallbackMessage: string;
  /** Optional external portal where the credentials are issued. */
  getKeyUrl?: string;
  /** External link text (default 'Get credentials'). */
  getKeyLabel?: string;
  /** Force the setup link regardless of the error shape. */
  alwaysShowSetup?: boolean;
}

// Heuristic: does this error look like "the integration isn't set up" rather
// than a transient/unexpected failure? 401/403/503 or a config-flavored message.
const CONFIG_ERROR_PATTERN =
  /not configured|not set up|no .*credential|missing .*credential|api key|invalid .*key|unauthor|provider .*not|not active|not valid|no .*provider/i;

function looksLikeConfigError(error: unknown, message: string): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status === 401 || status === 403 || status === 503) return true;
  return CONFIG_ERROR_PATTERN.test(message);
}

/**
 * Show an actionable error toast. If the failure looks like a missing-config
 * issue (or `alwaysShowSetup`), render the honest message + a link to the setup
 * page (+ optional external "get credentials" link). Otherwise a plain error.
 */
export function showIntegrationErrorToast(
  error: unknown,
  opts: IntegrationErrorToastOptions
): void {
  const message = extractApiErrorMessage(error, opts.fallbackMessage);

  if (!opts.alwaysShowSetup && !looksLikeConfigError(error, message)) {
    toast.error(message);
    return;
  }

  toast.error(
    (t) => (
      <div className="flex flex-col gap-2">
        <span>{message}</span>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => {
              toast.dismiss(t.id);
              opts.navigate(opts.setupPath);
            }}
            className="text-sm font-semibold text-blue-600 underline hover:text-blue-800"
          >
            {opts.setupLabel} →
          </button>
          {opts.getKeyUrl && (
            <a
              href={opts.getKeyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-gray-500 underline hover:text-gray-700"
            >
              {opts.getKeyLabel || 'Get credentials'}
            </a>
          )}
        </div>
      </div>
    ),
    { duration: 8000 }
  );
}
